import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { currencyRowsFromResponseData, normalizeCurrencyRow } from "../utils/currencyList";
import { createCompanyAccount } from "./companyAccountService";
import CompanyAccountPaymentFields, { upiIdError } from "../components/CompanyAccountPaymentFields";
import "../assets/css/Issues.css";
import "../assets/css/Projects.css";
import "../assets/css/ViewCustomer.css";
import { getAuthItem } from "../utils/authStorage";

const ACCOUNT_TYPES = ["Savings", "Current"];
const STATUS_OPTIONS = ["Active", "Inactive"];

const initialForm = {
  Account_Name: "",
  Account_Number: "",
  Account_Type: "Savings",
  IFSC_Code: "",
  Bank_Name: "",
  Branch: "",
  Currency_Id: "",
  UPI_Id: "",
  Issuer_Company_Id: "",
  Is_Primary: false,
  Status: "Active",
  Notes: ""
};

function getCreatedByFromStorage() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id ?? user.id ?? 1;
  } catch {
    return 1;
  }
}

function AddCompanyAccount() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCurrencies(true);
      try {
        const response = await axiosInstance.get("/currencies");
        const rows = currencyRowsFromResponseData(response.data);
        const mapped = rows.map(normalizeCurrencyRow).filter(Boolean);
        if (!cancelled) setCurrencies(mapped);
      } catch {
        if (!cancelled) setCurrencies([]);
      } finally {
        if (!cancelled) setLoadingCurrencies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goBack = () => {
    navigate("/finance-management", { state: { financeTab: "accounts" } });
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = (form.Account_Name || "").trim();
    const number = (form.Account_Number || "").trim();
    const bank = (form.Bank_Name || "").trim();
    const ifsc = (form.IFSC_Code || "").trim();
    if (!name || !number || !bank || !ifsc) {
      setSubmitError("Account name, account number, bank name, and IFSC are required.");
      return;
    }
    if (!form.Currency_Id) {
      setSubmitError("Please select a currency.");
      return;
    }
    const upiError = upiIdError(form.UPI_Id);
    if (upiError) {
      setSubmitError(upiError);
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      const payload = {
        Account_Name: name,
        Account_Number: number,
        Account_Type: form.Account_Type || "Savings",
        IFSC_Code: ifsc,
        Bank_Name: bank,
        Branch: (form.Branch || "").trim(),
        Currency_Id: Number(form.Currency_Id),
        UPI_Id: (form.UPI_Id || "").trim() || null,
        Issuer_Company_Id: form.Issuer_Company_Id ? Number(form.Issuer_Company_Id) : null,
        Is_Primary: form.Is_Primary ? 1 : 0,
        Status: form.Status || "Active",
        Notes: (form.Notes || "").trim(),
        Created_By: getCreatedByFromStorage()
      };
      await createCompanyAccount(payload);
      navigate("/finance-management", { replace: true, state: { financeTab: "accounts" } });
    } catch (err) {
      console.error(err);
      setSubmitError(
        err.response?.data?.message || err.response?.data?.error || "Failed to save company account."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="issues-container">
      <div className="viewlead-header" style={{ marginBottom: 12 }}>
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back 
        </button>
      </div>
      <div className="issues-main-card">
        <form className="issues-form" onSubmit={handleSubmit}>

          {submitError ? <div className="issues-alert issues-alert-error">{submitError}</div> : null}

          <div className="issues-form-grid">
            <div className="form-group">
              <label>
                Account name <span className="required">*</span>
              </label>
              <input
                name="Account_Name"
                value={form.Account_Name}
                onChange={handleFormChange}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>
                Account number <span className="required">*</span>
              </label>
              <input
                name="Account_Number"
                value={form.Account_Number}
                onChange={handleFormChange}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Account type</label>
              <select name="Account_Type" value={form.Account_Type} onChange={handleFormChange}>
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Bank name <span className="required">*</span>
              </label>
              <input name="Bank_Name" value={form.Bank_Name} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <input name="Branch" value={form.Branch} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>
                IFSC code <span className="required">*</span>
              </label>
              <input
                name="IFSC_Code"
                value={form.IFSC_Code}
                onChange={handleFormChange}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>
                Currency <span className="required">*</span>
              </label>
              <select
                name="Currency_Id"
                value={form.Currency_Id}
                onChange={handleFormChange}
                disabled={loadingCurrencies}
              >
                <option value="">{loadingCurrencies ? "Loading..." : "Select currency"}</option>
                {currencies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.code ? `${c.code} — ${c.name || c.code}` : c.name || `ID ${c.id}`}
                  </option>
                ))}
              </select>
            </div>
            <CompanyAccountPaymentFields form={form} onChange={handleFormChange} />
            <div className="form-group">
              <label>Status</label>
              <select name="Status" value={form.Status} onChange={handleFormChange}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  fontWeight: 500,
                  marginBottom: 0
                }}
              >
                <input type="checkbox" name="Is_Primary" checked={form.Is_Primary} onChange={handleFormChange} />
                Is Primary account
              </label>
            </div>
            <div className="form-group issues-description-full">
              <label>Notes</label>
              <textarea name="Notes" value={form.Notes} onChange={handleFormChange} rows={3} />
            </div>
          </div>

          <div className="issues-form-actions" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
           
            <button
              type="submit"
              className="btn-primary btn-add-lead btn-save-company-account"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCompanyAccount;
